/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Huu Khang Nguyen
 * Created: 03/02/22
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
    generateHistoryComment,
    mapWorkplanStageToString,
    Workplan,
    WorkplanStage,
    WorkplanStateHistory,
    WorkplanVersion,
} from '@/wpt-lib/interfaces/Workplan';
import { Comment } from '@/wpt-lib/interfaces/Comment';
import { Flex, HStack, Select, Tab, TabList, Tabs, Text, VStack } from '@chakra-ui/react';
import { CommentsTile } from '@/components/pages/workplan/comment/CommentsTile';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { WorkplanComment, WorkplanCommentSection } from '@/wpt-lib/interfaces/WorkplanComment';

interface ReviewCommentsTileProps {
    isReadonly: boolean;
    currentAssignment?: Assignment;
    workplan: Workplan;
    workplanVersion: WorkplanVersion;
}

type TabDataProps = {
    id: number;
    title: string;
};

const ReviewCommentTile = ({ isReadonly, currentAssignment, workplan, workplanVersion }: ReviewCommentsTileProps) => {
    const [stage, setStage] = useState<WorkplanStage | 'All'>(
        workplanVersion.stage === WorkplanStage.CLOSED ? WorkplanStage.END_YEAR : workplanVersion.stage,
    );

    const tabData: TabDataProps[] = [
        { id: 0, title: 'All' },
        { id: 1, title: 'Comments' },
        { id: 2, title: 'Updates' },
    ];

    const [selectedTab, setSelectedTab] = useState<TabDataProps>(tabData[0]);
    const changeTab = (tabId: number) => {
        setSelectedTab(tabData[tabId]);
    };

    const { makeRequest, responseData: history } = useGetRequest<WorkplanStateHistory[]>();

    useEffect(() => {
        makeRequest(`workplan/${workplan.id}/history`);
    }, [workplan]);

    const stateComments = useMemo<(Comment & { readonly: boolean })[]>(
        () =>
            history
                ?.filter((history) => history.version <= (workplanVersion.version ?? Number.MAX_VALUE))
                .map((history) => ({
                    id: 0,
                    createdAt: history.createdAt,
                    updatedAt: history.createdAt,
                    comment: generateHistoryComment(history),
                    approved: true,
                    readonly: true,
                    author: history.assignment,
                    stage: history.fromStage,
                })) ?? [],
        [history, workplanVersion],
    );

    const placeholders: Record<number, string> = {
        0: 'No comments or updates',
        1: 'No comments',
        2: 'No updates',
    };

    return (
        <Flex py={4} overflowY={'auto'}>
            <VStack w={'full'} spacing={4} alignItems={'flex-start'}>
                <Tabs variant={'enclosed'} isLazy index={selectedTab.id} onChange={changeTab} w="full">
                    <HStack w={'full'} justifyContent={'flex-start'} gap={8}>
                        <HStack>
                            <Text id="stage-menu">Comments from Stage: </Text>
                            <Select
                                aria-labelledby="stage-menu"
                                w={'auto'}
                                value={stage}
                                onChange={(e) => setStage(e.currentTarget.value as WorkplanStage | 'All')}
                                role="combobox"
                            >
                                <option key={'All'} value={'All'}>
                                    All
                                </option>
                                {Object.values(WorkplanStage).map((stage) => (
                                    <option key={stage} value={stage}>
                                        {mapWorkplanStageToString[stage]}
                                    </option>
                                ))}
                            </Select>
                        </HStack>
                        <TabList px={6} gridGap={6}>
                            {tabData.map((tab) => (
                                <Tab
                                    key={tab.id}
                                    tabIndex={tab.id}
                                    border={'1px'}
                                    borderColor={'gray.200'}
                                    fontWeight={tab === selectedTab ? 'semibold' : 'normal'}
                                >
                                    {tab.title}
                                </Tab>
                            ))}
                        </TabList>
                    </HStack>

                    <CommentsTile
                        isReadonly={isReadonly}
                        currentAssignment={currentAssignment}
                        baseApiUrl={`workplan/${workplan.id}`}
                        apiParams={{ all: true }}
                        workplan={workplan}
                        version={workplanVersion.version}
                        system={stateComments}
                        data={{ stage: workplanVersion.stage }}
                        filter={(comment) => {
                            const workplanComment = comment as WorkplanComment;
                            return (
                                (!workplanComment.section ||
                                    [
                                        WorkplanCommentSection.END_YEAR,
                                        WorkplanCommentSection.END_YEAR_SUPERSEDED,
                                    ].includes(workplanComment.section)) && // Filter out other sections
                                (stage === 'All' || workplanComment.stage === stage) && // Filter on stage
                                (selectedTab.id === 0 || // Filter on comment or updates tab
                                    (selectedTab.id === 1 && comment.id > 0) ||
                                    (selectedTab.id === 2 && comment.id === 0))
                            );
                        }}
                        onCreate={() => setStage((stage) => (stage === 'All' ? 'All' : workplanVersion.stage))}
                        placeholder={placeholders[selectedTab.id]}
                    />
                </Tabs>
            </VStack>
        </Flex>
    );
};
export default ReviewCommentTile;
